(() => {
  class HeaderSearchModal {
    constructor(dialog) {
      this.dialog = dialog;
      this.form = dialog.querySelector('[data-search-form]');
      this.input = dialog.querySelector('[data-search-input]');
      this.results = dialog.querySelector('[data-search-results]');
      this.hint = dialog.querySelector('[data-search-hint]');
      this.status = dialog.querySelector('[data-search-status]');
      this.clearButton = dialog.querySelector('[data-search-clear]');
      this.closeButton = dialog.querySelector('[data-search-modal-close]');
      this.endpoint = dialog.dataset.predictiveSearchUrl;
      this.cache = new Map();
      this.abortController = null;
      this.debounceTimer = null;
      this.activeIndex = -1;
      this.opener = null;
      this.bindEvents();
      this.updateClearButton();
    }

    bindEvents() {
      document.querySelectorAll('[data-search-modal-open]').forEach((trigger) => {
        if (trigger.dataset.searchModalOpen !== this.dialog.id) return;
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      });

      this.closeButton.addEventListener('click', () => this.dialog.close());
      this.clearButton.addEventListener('click', () => this.clear());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.results.addEventListener('mousemove', (event) => this.onResultHover(event));

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('close', () => {
        this.input.setAttribute('aria-expanded', 'false');
        this.setActiveOption(-1);
        if (this.opener) this.opener.setAttribute('aria-expanded', 'false');
      });
    }

    open(trigger) {
      if (typeof this.dialog.showModal !== 'function') {
        window.location.href = trigger.href;
        return;
      }

      this.opener = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      if (!this.dialog.open) this.dialog.showModal();
      window.requestAnimationFrame(() => this.input.focus());

      if (this.input.value.trim().length >= 3 && this.results.innerHTML.trim() === '') {
        this.requestResults();
      } else if (!this.results.hidden) {
        this.input.setAttribute('aria-expanded', 'true');
      }
    }

    onInput() {
      window.clearTimeout(this.debounceTimer);
      this.abortController?.abort();
      this.updateClearButton();
      this.setActiveOption(-1);

      const term = this.input.value.trim();
      if (term.length < 3) {
        this.hideResults();
        this.setHint('Enter at least 3 characters to see suggestions.', false);
        this.setStatus('');
        return;
      }

      this.setHint('Searching…', false);
      this.debounceTimer = window.setTimeout(() => this.requestResults(), 300);
    }

    async requestResults() {
      const term = this.input.value.trim();
      if (term.length < 3) return;

      const cacheKey = term.toLocaleLowerCase();
      if (this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey));
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', 'title');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][prefix]', 'last');
      this.results.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: this.abortController.signal,
        });
        const responseText = await response.text();
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const renderedSection = responseDocument.querySelector('#shopify-section-predictive-search');
        if (!renderedSection) throw new Error('Predictive search section was not returned');

        const html = renderedSection.innerHTML;
        this.cache.set(cacheKey, html);
        if (term === this.input.value.trim()) this.renderResults(html);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hideResults();
          this.setHint('Suggestions are unavailable. Press Search to continue.', false);
          this.setStatus('Search suggestions are unavailable.');
          console.error(error);
        }
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }

    renderResults(html) {
      this.results.innerHTML = html;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setActiveOption(-1);

      const productCount = this.results.querySelectorAll('.predictive-product-card').length;
      if (productCount > 0) {
        this.setHint('', true);
        this.setStatus(`${productCount} product suggestion${productCount === 1 ? '' : 's'} available.`);
      } else {
        this.setHint('No matching products. Press Search to view all results.', false);
        this.setStatus('No matching product suggestions.');
      }
    }

    onKeydown(event) {
      const options = this.options;
      if (event.key === 'ArrowDown' && options.length > 0) {
        event.preventDefault();
        this.setActiveOption((this.activeIndex + 1) % options.length);
      } else if (event.key === 'ArrowUp' && options.length > 0) {
        event.preventDefault();
        this.setActiveOption(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex].click();
      } else if (event.key === 'Escape' && this.dialog.open) {
        event.preventDefault();
        this.dialog.close();
      }
    }

    onResultHover(event) {
      const option = event.target.closest('[role="option"]');
      if (!option || !this.results.contains(option)) return;
      this.setActiveOption(this.options.indexOf(option));
    }

    setActiveOption(index) {
      const options = this.options;
      options.forEach((option) => option.removeAttribute('aria-selected'));
      this.activeIndex = index;

      if (index >= 0 && options[index]) {
        options[index].setAttribute('aria-selected', 'true');
        this.input.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    clear() {
      window.clearTimeout(this.debounceTimer);
      this.abortController?.abort();
      this.input.value = '';
      this.updateClearButton();
      this.hideResults();
      this.setHint('Enter at least 3 characters to see suggestions.', false);
      this.setStatus('Search cleared.');
      this.input.focus();
    }

    hideResults() {
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.setActiveOption(-1);
    }

    updateClearButton() {
      this.clearButton.hidden = this.input.value.length === 0;
    }

    setHint(message, hidden) {
      this.hint.textContent = message;
      this.hint.hidden = hidden;
    }

    setStatus(message) {
      this.status.textContent = message;
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }
  }

  const initializeSearchModals = (root = document) => {
    root.querySelectorAll('[data-search-modal]').forEach((dialog) => {
      if (dialog.searchModalController) return;
      dialog.searchModalController = new HeaderSearchModal(dialog);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeSearchModals());
  } else {
    initializeSearchModals();
  }

  document.addEventListener('shopify:section:load', (event) => initializeSearchModals(event.target));
})();
